export const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3000";

/** Field-keyed validation errors, shaped to drop straight into react-hook-form. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
  }
}

// The access token lives in memory only: a 15-minute token in localStorage
// buys an attacker very little, but there is no reason to leave it there.
let accessToken: string | null = null;
export const setAccessToken = (t: string | null) => void (accessToken = t);
export const getAccessToken = () => accessToken;

const REFRESH_KEY = "garage.refreshToken";
export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);
export const setRefreshToken = (t: string | null) =>
  t ? localStorage.setItem(REFRESH_KEY, t) : localStorage.removeItem(REFRESH_KEY);

let onSessionLost: (() => void) | null = null;
export const setSessionLostHandler = (fn: () => void) => void (onSessionLost = fn);

async function parse(res: Response) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(
      res.status,
      body.error ?? `Request failed (${res.status})`,
      body.code,
      body.fields,
    );
  }
  return body;
}

/**
 * Refreshing is deduplicated: several requests can 401 at once when a token
 * expires mid-page, and without this each would rotate the refresh token,
 * invalidating the others and logging the user out.
 */
let refreshing: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  refreshing ??= (async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const body = await res.json();
      setAccessToken(body.accessToken);
      setRefreshToken(body.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so concurrent callers all see this result.
      setTimeout(() => (refreshing = null), 0);
    }
  })();

  return refreshing;
}

interface Options {
  method?: string;
  body?: unknown;
  auth?: boolean;
}

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const { method = "GET", body, auth = true } = opts;

  const send = () => {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (auth && accessToken) headers.Authorization = `Bearer ${accessToken}`;
    return fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  };

  // After a reload the access token is gone (it lives in memory) but the
  // refresh token survives. Trading it in first avoids a guaranteed 401 on
  // every single page load.
  if (auth && !accessToken && getRefreshToken()) {
    await refreshSession();
  }

  let res = await send();

  // One retry, and only for an expired token — never for bad credentials.
  if (res.status === 401 && auth && getRefreshToken()) {
    if (await refreshSession()) {
      res = await send();
    } else {
      setAccessToken(null);
      setRefreshToken(null);
      onSessionLost?.();
    }
  }

  return parse(res) as Promise<T>;
}

export interface Health {
  ok: boolean;
  service: string;
  env: string;
  db: "up" | "down" | "not-configured";
  storageConfigured: boolean;
  rcLookupConfigured: boolean;
}
